/**
 * Tiny client-side A/B harness.
 *
 * Assigns a stable variant per visitor on first visit and stores it in
 * localStorage so the same browser sees the same variant across reloads.
 * Variant tags are forwarded with checkout requests so the backend can record
 * which variant the conversion came from.
 *
 * For server-driven experiments (hero_headline), the current LIVE variants
 * and their weights are fetched from the backend once on load and cached
 * in-memory + localStorage. When the backend retires a variant and promotes
 * a challenger, existing visitors whose sticky assignment is no longer live
 * are re-bucketed on their next visit.
 *
 * Add a new test by registering it in EXPERIMENTS below and (optionally)
 * marking it `serverDriven: true` if the backend owns the live weights.
 */

const STORAGE_KEY = "pp_ab_variants_v1";
const CONFIG_CACHE_KEY = "pp_ab_config_v1";

// Experiment registry. Each experiment has an `id`, ordered `variants` array
// and a fixed weight per variant (must sum to 1.0). Variants are sticky.
// Server-driven experiments have their `variants` replaced at runtime from
// `/api/ab/{experiment}` — the local values here are only the initial seed.
const EXPERIMENTS = {
  pro_callout: {
    variants: [
      { id: "A", weight: 0.5 }, // control — no callout
      { id: "B", weight: 0.5 }, // treatment — "Save $46" callout
    ],
  },
  hero_headline: {
    serverDriven: true,
    endpoint: "/api/ab/hero-headline",
    variants: [
      { id: "A", weight: 0.5 }, // control — "Step into the tank..."
      { id: "E", weight: 0.5 }, // challenger — "Everyone says your idea is interesting..."
    ],
  },
};

function loadStore() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveStore(store) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage may be disabled (incognito, etc.) — fall through */
  }
}

function loadConfigCache() {
  try {
    return JSON.parse(window.localStorage.getItem(CONFIG_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveConfigCache(cache) {
  try {
    window.localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* silent */
  }
}

function pickVariant(experiment) {
  const r = Math.random();
  let acc = 0;
  for (const v of experiment.variants) {
    acc += v.weight;
    if (r <= acc) return v.id;
  }
  return experiment.variants[experiment.variants.length - 1].id;
}

/**
 * Merge server-provided variants into the in-memory EXPERIMENTS entry.
 * Called on module load (from cache) and after the server fetch resolves.
 */
function applyServerConfig(experimentId, serverVariants) {
  if (!EXPERIMENTS[experimentId] || !Array.isArray(serverVariants)) return;
  if (serverVariants.length === 0) return;
  EXPERIMENTS[experimentId].variants = serverVariants.map((v) => ({
    id: String(v.id),
    weight: Number(v.weight) || 0,
  }));
}

// Seed each server-driven experiment from the localStorage cache immediately
// so the first render doesn't wait for the network.
if (typeof window !== "undefined") {
  const cache = loadConfigCache();
  for (const [id, exp] of Object.entries(EXPERIMENTS)) {
    if (exp.serverDriven && cache[id]?.variants) {
      applyServerConfig(id, cache[id].variants);
    }
  }
}

// Fire-and-forget: refresh server-driven experiments in the background.
// Successful responses update in-memory config AND the localStorage cache
// so the next page load starts with the freshest weights.
async function refreshServerConfigs() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend || typeof window === "undefined") return;
  const cache = loadConfigCache();
  for (const [id, exp] of Object.entries(EXPERIMENTS)) {
    if (!exp.serverDriven || !exp.endpoint) continue;
    try {
      const res = await fetch(`${backend}${exp.endpoint}`, { credentials: "omit" });
      if (!res.ok) continue;
      const body = await res.json();
      if (Array.isArray(body?.variants) && body.variants.length > 0) {
        applyServerConfig(id, body.variants);
        cache[id] = { variants: body.variants, updated_at: body.updated_at };
      }
    } catch {
      /* network failure — keep local weights until next refresh */
    }
  }
  saveConfigCache(cache);
}
if (typeof window !== "undefined") {
  refreshServerConfigs();
}

/** Get the assigned variant for an experiment (assigns if first time, or if
 *  the previously-assigned variant is no longer in the current live list). */
export function getVariant(experimentId) {
  const exp = EXPERIMENTS[experimentId];
  if (!exp) return null;
  if (typeof window === "undefined") return exp.variants[0].id;
  const store = loadStore();
  const liveIds = new Set(exp.variants.map((v) => v.id));
  if (!store[experimentId] || !liveIds.has(store[experimentId])) {
    store[experimentId] = pickVariant(exp);
    saveStore(store);
  }
  return store[experimentId];
}

/** Get all variants the visitor has been assigned (for sending with conversions). */
export function getAllVariants() {
  if (typeof window === "undefined") return {};
  return loadStore();
}
